import React, { useCallback, useMemo } from 'react';
import { Text, View, ScrollView } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import InputSection from '@/components/General/inputs/InputSection';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SolidButton from '@/components/General/SolidButton';
import FormRolesPicker from '@/components/Forms/FormRolesPicker';
import FormResourcesEditor from '@/components/Forms/builder/FormResourcesEditor';
import { Query } from '@/utils/react-query/Queries';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import { buildFolderTree, flattenFolderTree, folderPath } from '@/utils/formFolders';
import { schemaHasFieldType } from '@/utils/formsSchema';
import { normalizeResourcePaths } from '@/utils/formResourcePolicy';

/**
 * Settings tab — form-level options. Reads/writes the working `details` object
 * via `onDetailsChange`. `onSave` persists. Editing gated by `canEdit`.
 */
const FormSettingsTab = ({ details, onDetailsChange, canEdit, onCreatePathForForm, creatingPath }) => {
    const { theme: { colors } } = useUnistyles();
    const router = useRouter();

    const update = useCallback((patch) => {
        onDetailsChange({ ...details, ...patch });
    }, [details, onDetailsChange]);

    const updateAction = useCallback((actionType, patch) => {
        const actions = { ...(details.actions || {}) };
        const current = actions[actionType] || { enabled: false };
        actions[actionType] = { ...current, ...patch };
        update({ actions });
    }, [details, update]);

    const selectedRoles = details.Roles || [];
    const formFoldersQuery = Query.formFolders.all();
    const formFolders = formFoldersQuery.data || [];
    const folderChoices = useMemo(() => flattenFolderTree(buildFolderTree(formFolders)).map((folder) => ({
        ...folder,
        name: folderPath(formFolders, folder.id),
    })), [formFolders]);
    const selectedFolder = folderChoices.find((folder) => folder.id === details.folderID) || null;
    const folderQuery = { ...formFoldersQuery, data: folderChoices };

    // Fetch paths with FORM_ENTRY trigger for the Trigger Path picker
    const formTriggerPathsQuery = Query.paths.forFormTrigger();
    const formTriggerPaths = formTriggerPathsQuery.data;

    // Resolve the currently selected path object (for SheetSelector display)
    const selectedPath = useMemo(() => {
        const pathId = details.actions?.triggerPath?.pathId;
        if (!pathId || !formTriggerPaths) return null;
        const found = formTriggerPaths.find((p) => p.id === pathId);
        return found || null;
    }, [details.actions?.triggerPath?.pathId, formTriggerPaths]);

    const handlePathSelect = useCallback((selected) => {
        if (selected?.id) {
            updateAction('triggerPath', { pathId: selected.id });
        } else {
            updateAction('triggerPath', { pathId: null });
        }
    }, [updateAction]);

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            <InputSection title="Form Details">
                <Input
                    title="Title"
                    value={details.title || ''}
                    onUpdate={(val) => update({ title: val })}
                    disabled={!canEdit}
                    placeholder="Form title"
                />
                <Input
                    title="Description"
                    value={details.description || ''}
                    onUpdate={(val) => update({ description: val })}
                    multiline
                    disabled={!canEdit}
                    placeholder="Shown to people filling out the form"
                />
                <SheetSelector
                    title="Folder"
                    placeholder="Uncategorized"
                    helpText="Folders organize the library only and never change who can access this form."
                    type="roleFolders"
                    queryResult={folderQuery}
                    value={selectedFolder}
                    onChange={(folder) => update({ folderID: folder?.id || null })}
                    disabled={!canEdit}
                    addScreenName={canEdit ? '/forms/folders/create' : null}
                />
            </InputSection>

            <InputSection title="Submission">
                <ToggleRow
                    label="Send Notifications for Entries"
                    description="Notify the form's creator and everyone with edit access. Each person chooses push or email in their notification settings."
                    value={details.sendNotificationsForEntries ?? false}
                    onValueChange={(val) => update({ sendNotificationsForEntries: val })}
                    disabled={!canEdit}
                    testID="form-entry-notifications-toggle"
                />
                <Input
                    title="Submit Button Text"
                    value={details.submitButtonText || ''}
                    onUpdate={(val) => update({ submitButtonText: val })}
                    disabled={!canEdit}
                    placeholder="Submit"
                />
                <Input
                    title="Success Message"
                    value={details.successMessage || ''}
                    onUpdate={(val) => update({ successMessage: val })}
                    multiline
                    disabled={!canEdit}
                    placeholder="Thanks! Your response has been recorded."
                />
            </InputSection>

            <InputSection title="Access">
                <ToggleRow
                    label="Public Form"
                    description="When on, this form gets a shareable public link anyone can fill out without logging in."
                    value={details.isPublic ?? false}
                    onValueChange={(val) => {
                        // Directory-dependent fields can't exist on a public
                        // form — block the toggle instead of silently hiding
                        // or deleting the creator's fields.
                        if (val === true) {
                            // Profile selectors need the authenticated people
                            // directory — the only field type banned on public.
                            if (schemaHasFieldType(details.schema, 'profile')) {
                                Toast.show({ type: 'error', text2: 'Remove profile selector fields before making this form public.' });
                                return;
                            }
                            // Attached documents become anonymous-readable once
                            // the form is public — warn (don't block).
                            if (normalizeResourcePaths(details.resourcePaths).length > 0) {
                                Toast.show({ type: 'info', text2: 'This form has attached documents — anyone with the public link can open them.' });
                            }
                        }
                        update({ isPublic: val });
                    }}
                    disabled={!canEdit}
                    testID="form-public-toggle"
                />
            </InputSection>

            <FormRolesPicker
                value={selectedRoles}
                onChange={(roles) => update({ Roles: roles })}
                disabled={!canEdit}
                isPublic={details.isPublic ?? false}
            />

            <InputSection title="People">
                <ToggleRow
                    label="Auto-Create New People"
                    description="When on, a new Person record is created automatically when a submission has no name match. Name field must be present. If this is off, you can still manually create people from entries."
                    value={details.autoCreatePeople ?? false}
                    onValueChange={(val) => update({ autoCreatePeople: val })}
                    disabled={!canEdit}
                />
            </InputSection>

            <InputSection title="Resources" testID="form-resources-section">
                <FormResourcesEditor
                    formID={details.id}
                    resourcePaths={Array.isArray(details.resourcePaths) ? details.resourcePaths : []}
                    onChange={(paths) => update({ resourcePaths: paths })}
                    disabled={!canEdit}
                    isPublic={details.isPublic ?? false}
                />
            </InputSection>

            <InputSection title="Additional Actions">
                <Text style={styles.sectionHelp(colors)}>
                    Optional actions that run server-side after each submission. One of each type max.
                </Text>

                {/* Redirect */}
                <View style={styles.actionBlock} testID="form-action-redirect">
                    <ToggleRow
                        label="Redirect"
                        description="Send the filler to a URL after submitting."
                        value={details.actions?.redirect?.enabled ?? false}
                        onValueChange={(val) => updateAction('redirect', { enabled: val })}
                        disabled={!canEdit}
                    />
                    {details.actions?.redirect?.enabled && (
                        <View style={styles.actionConfig}>
                            <Input
                                title="Redirect URL"
                                value={details.actions?.redirect?.url || ''}
                                onUpdate={(val) => updateAction('redirect', { url: val })}
                                disabled={!canEdit}
                                placeholder="https://example.com/thanks"
                            />
                        </View>
                    )}
                </View>

                {/* Trigger Path — "Run Automation" semantic (§14.5): this
                    legacy pathID binding starts an automation run. A Follow
                    Up path is a different product — started per entry from
                    the Entries tab. */}
                <View style={styles.actionBlock} testID="form-action-triggerPath">
                    <ToggleRow
                        label="Run Automation"
                        description="Start an automation run for the submitter when this form is submitted. To start a Follow Up path for an entry instead, use the Entries tab."
                        value={details.actions?.triggerPath?.enabled ?? false}
                        onValueChange={(val) => updateAction('triggerPath', { enabled: val })}
                        disabled={!canEdit}
                    />
                    {details.actions?.triggerPath?.enabled && (
                        <View style={styles.actionConfig}>
                            <SheetSelector
                                title="Automation"
                                placeholder="Select an Automation"
                                helpText="Select an automation that has a 'Form Entry' trigger. If you don't have one yet, use the button below to get started"
                                type="paths"
                                queryResult={formTriggerPathsQuery}
                                value={selectedPath}
                                onChange={handlePathSelect}
                                disabled={!canEdit}
                            />
                            {canEdit && onCreatePathForForm && (
                                <View style={styles.pathActions}>
                                    <SolidButton
                                        text="Create a New Automation"
                                        onPress={onCreatePathForForm}
                                        processing={creatingPath}
                                        type="forms"
                                        variant="muted"
                                        size="sm"
                                        iconName="add-outline"
                                        iconBefore
                                        testID="form-create-new-path"
                                    />
                                    {selectedPath && (
                                        <SolidButton
                                            text="Edit Selected Automation"
                                            onPress={() => router.push(`/automations/${selectedPath.id}`)}
                                            type="textSecondary"
                                            variant="muted"
                                            size="sm"
                                            iconName="create-outline"
                                            iconBefore
                                            testID="form-edit-path"
                                        />
                                    )}
                                </View>
                            )}
                        </View>
                    )}
                </View>
            </InputSection>

        </ScrollView>
    );
};

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    content: {
        padding: 16,
        maxWidth: 720,
        alignSelf: 'center',
        width: '100%',
        paddingBottom: 100,
    },
    sectionHelp: (colors) => ({
        fontSize: 13,
        color: colors.textSecondary,
        marginBottom: 8,
        marginLeft: 4,
    }),
    actionBlock: {
        paddingVertical: 6,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    actionConfig: { marginLeft: 4, marginTop: 4, gap: 8 },
    pathActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
}));

export default FormSettingsTab;
